import { useState, useEffect, useCallback } from 'react';
import * as db from '@/lib/data-service';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Users, UserCheck, UserPlus, Heart, MapPin, RefreshCw, Loader2, Check, X, AlertTriangle, Camera, Key, Plus, Trash2, Copy } from 'lucide-react';
import { toast } from 'sonner';

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;

async function getProfilePhoto(profileId) {
  try {
    const { data } = await supabase
      .from('photos')
      .select('url')
      .eq('profile_id', profileId)
      .eq('position', 0)
      .limit(1);
    const url = data?.[0]?.url;
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return supabase.storage.from('profile-photos').getPublicUrl(url)?.data?.publicUrl || null;
  } catch {
    return null;
  }
}

async function getSelfieUrl(profile) {
  const source = profile.selfie_path || profile.selfie_code;
  if (!source) return null;
  if (source.startsWith('http')) return source;
  try {
    const { data } = await supabase.storage
      .from('profile-selfies')
      .createSignedUrl(source, 3600);
    return data?.signedUrl || supabase.storage.from('profile-selfies').getPublicUrl(source)?.data?.publicUrl || null;
  } catch {
    return supabase.storage.from('profile-selfies').getPublicUrl(source)?.data?.publicUrl || null;
  }
}

export default function Admin() {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [stats, setStats] = useState(null);
  const [selfieQueue, setSelfieQueue] = useState([]);
  const [reportedProfiles, setReportedProfiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const [referralCodes, setReferralCodes] = useState([]);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [confirmDeleteCode, setConfirmDeleteCode] = useState(null);

  const [reviewing, setReviewing] = useState(null);
  const [confirmDecline, setConfirmDecline] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('jiv_admin_session') === 'true') setAuthenticated(true);
  }, []);

  const fetchSelfieQueue = async () => {
    // Profiles where selfie is submitted but not yet verified
    const { data, error } = await supabase
      .from('profiles')
      .select('id, display_name, last_initial, city, sect, vouch_status, selfie_path, selfie_code, selfie_submitted_at, selfie_verified')
      .or('selfie_verified.is.null,selfie_verified.eq.false')
      .not('selfie_submitted_at', 'is', null)
      .neq('admin_blocked', true)
      .order('selfie_submitted_at', { ascending: true });

    if (error) throw error;
    const rows = data || [];

    return Promise.all(rows.map(async (p) => ({
      ...p,
      cover_url: await getProfilePhoto(p.id),
      selfie_url: await getSelfieUrl(p),
    })));
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsData, selfieData, reportedData, codesData] = await Promise.all([
        db.getAdminStats(),
        fetchSelfieQueue(),
        db.getReportedProfiles(),
        db.getReferralCodes(),
      ]);
      setStats(statsData);
      setSelfieQueue(selfieData);
      setReportedProfiles(reportedData);
      setReferralCodes(codesData);
    } catch (e) {
      console.error('Admin fetch error:', e);
      toast('Error loading admin data');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (authenticated) fetchData();
  }, [authenticated, fetchData]);

  const openReview = async (profile) => {
    const selfie_url = profile.selfie_url || await getSelfieUrl(profile);
    const cover_url = profile.cover_url || await getProfilePhoto(profile.id);
    setReviewing({ ...profile, selfie_url, cover_url });
  };

  const closeReview = () => {
    setReviewing(null);
    setConfirmDecline(false);
  };

  const approveSelfie = async () => {
    if (!reviewing) return;
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ selfie_verified: true })
        .eq('id', reviewing.id);
      if (error) throw error;
      toast(`${reviewing.display_name} selfie approved`);
      closeReview();
      fetchData();
    } catch {
      toast('Error approving selfie');
    }
  };

  const declineSelfie = async () => {
    if (!reviewing) return;
    try {
      if (reviewing.selfie_path) {
        await supabase.storage.from('profile-selfies').remove([reviewing.selfie_path]);
      }
      const { error } = await supabase
        .from('profiles')
        .update({ selfie_path: null, selfie_code: null, selfie_submitted_at: null })
        .eq('id', reviewing.id);
      if (error) throw error;
      toast(`${reviewing.display_name}'s selfie rejected — they'll be asked to resubmit`);
      closeReview();
      fetchData();
    } catch {
      toast('Error declining selfie');
    }
  };

  const refreshReportedProfiles = useCallback(async () => {
    const data = await db.getReportedProfiles();
    setReportedProfiles(data);
  }, []);

  const handleGenerateCode = async () => {
    setGeneratingCode(true);
    const result = await db.generateReferralCode();
    setGeneratingCode(false);
    if (result.success) {
      toast(`Code generated: ${result.code}`);
      const codes = await db.getReferralCodes();
      setReferralCodes(codes);
    } else {
      console.error('[generateReferralCode]', result.error);
      toast(`Error: ${result.error}`);
    }
  };

  const handleDeleteCode = async () => {
    if (!confirmDeleteCode) return;
    await db.deleteReferralCode(confirmDeleteCode.id);
    setConfirmDeleteCode(null);
    const codes = await db.getReferralCodes();
    setReferralCodes(codes);
  };

  const handleLogin = () => {
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      localStorage.setItem('jiv_admin_session', 'true');
      setLoginError('');
    } else {
      setLoginError('Incorrect password');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('jiv_admin_session');
    setAuthenticated(false);
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6" data-testid="admin-login">
        <div className="w-full max-w-sm space-y-4">
          <h1 className="text-2xl font-bold text-gray-900">Jiv Admin</h1>
          <Input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            placeholder="Admin password"
            className="h-11 rounded-lg border-gray-300"
            data-testid="admin-password-input"
          />
          <Button
            onClick={handleLogin}
            className="w-full h-11 bg-[#C857A8] hover:bg-[#B54E9A] text-white rounded-lg"
            data-testid="admin-login-btn"
          >
            Log in
          </Button>
          {loginError && <p className="text-red-600 text-sm" data-testid="admin-error">{loginError}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="admin-dashboard">
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold">Jiv Admin</h1>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchData}
              disabled={loading}
              className="text-gray-600"
              data-testid="admin-refresh-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="text-gray-600" data-testid="admin-logout-btn">
              Logout
            </Button>
          </div>
        </div>

        {loading && !stats && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            <StatCard icon={<Users className="h-4 w-4" />} label="Total users" value={stats.totalUsers} testId="stat-total" />
            <StatCard icon={<UserCheck className="h-4 w-4" />} label="Verified" value={stats.verifiedUsers} testId="stat-verified" />
            <StatCard icon={<UserPlus className="h-4 w-4" />} label="New (7d)" value={stats.newSignups} testId="stat-new" />
            <StatCard icon={<Heart className="h-4 w-4" />} label="Matches" value={stats.totalMatches} testId="stat-matches" />
          </div>
        )}

        {/* Top cities */}
        {stats?.topCities?.length > 0 && (
          <div className="mb-10">
            <h2 className="text-base font-semibold mb-3">Top cities</h2>
            <div className="space-y-1">
              {stats.topCities.map(c => (
                <div key={c.city} className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-sm">{c.city}</span>
                  </div>
                  <span className="text-sm font-medium">{c.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Referral Codes */}
        <section className="mb-12">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Key className="h-5 w-5 text-[#C857A8]" />
                Referral Codes
                <span className="text-sm font-normal text-gray-400">({referralCodes.length})</span>
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">Single-use codes. Claimed codes are deleted automatically.</p>
            </div>
            <Button
              onClick={handleGenerateCode}
              disabled={generatingCode}
              size="sm"
              className="bg-[#C857A8] hover:bg-[#B54E9A] text-white rounded-lg shrink-0"
              data-testid="generate-code-btn"
            >
              {generatingCode
                ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                : <Plus className="h-3.5 w-3.5 mr-1" />}
              Generate Code
            </Button>
          </div>

          {referralCodes.length === 0 ? (
            <p className="text-sm text-gray-400" data-testid="no-referral-codes">
              No referral codes generated yet. Click Generate Code to create one.
            </p>
          ) : (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Code</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Created</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Status</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {referralCodes.map(rc => (
                    <ReferralCodeRow key={rc.id} rc={rc} onDelete={() => setConfirmDeleteCode(rc)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Section 1: Selfie Verification */}
        <section className="mb-12">
          <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
            <Camera className="h-5 w-5 text-[#C857A8]" />
            Selfie Verification
            <span className="text-sm font-normal text-gray-400">({selfieQueue.length})</span>
          </h2>
          <p className="text-xs text-gray-400 mb-4">
            Users who submitted a selfie awaiting your approval. Compare profile photo vs selfie.
          </p>

          {loading && selfieQueue.length === 0 ? (
            <p className="text-gray-400 text-sm">Loading...</p>
          ) : selfieQueue.length === 0 ? (
            <p className="text-gray-400 text-sm" data-testid="no-selfie-queue">No selfies pending review</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {selfieQueue.map(p => (
                <SelfieCard key={p.id} profile={p} onReview={() => openReview(p)} />
              ))}
            </div>
          )}
        </section>

        {/* Section 2: Reported Profiles */}
        <section>
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Reported Profiles
              <span className="text-sm font-normal text-gray-400">({reportedProfiles.length})</span>
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={refreshReportedProfiles}
              className="text-gray-400 hover:text-gray-600"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-gray-400 mb-4">
            Profiles flagged by users. Suspend to block them from the app.
          </p>

          {loading && reportedProfiles.length === 0 ? (
            <p className="text-gray-400 text-sm">Loading...</p>
          ) : reportedProfiles.length === 0 ? (
            <p className="text-gray-400 text-sm" data-testid="no-reported">No reported profiles</p>
          ) : (
            <div className="max-h-[600px] overflow-y-auto pr-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {reportedProfiles.map(p => (
                  <ReportedCard key={p.id} profile={p} onRefresh={refreshReportedProfiles} />
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Selfie Review Modal */}
      <Dialog open={!!reviewing} onOpenChange={closeReview}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle>Review Selfie</DialogTitle>
                <DialogDescription>
                  {reviewing?.display_name} {reviewing?.last_initial} · {reviewing?.city}
                  <span className="block font-mono text-xs mt-0.5 opacity-60">{reviewing?.id}</span>
                </DialogDescription>
              </div>
              <button
                onClick={handleLogout}
                className="text-xs text-gray-400 hover:text-gray-600 shrink-0 ml-4 mt-0.5"
              >
                Log out
              </button>
            </div>
          </DialogHeader>

          {confirmDecline ? (
            <div className="space-y-4">
              <p className="text-sm font-medium text-gray-900">Reject this selfie?</p>
              <p className="text-sm text-gray-500">The user will be asked to retake their selfie.</p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setConfirmDecline(false)} className="rounded-lg">
                  Back
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => { declineSelfie(); setConfirmDecline(false); }}
                  className="rounded-lg"
                >
                  Reject
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1.5">Profile photo</p>
                  {reviewing?.cover_url ? (
                    <img
                      src={reviewing.cover_url}
                      alt="Profile"
                      className="w-full aspect-[3/4] object-cover rounded-lg bg-gray-100"
                    />
                  ) : (
                    <div className="w-full aspect-[3/4] rounded-lg bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                      No photo
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1.5">Submitted selfie</p>
                  {reviewing?.selfie_url ? (
                    <img
                      src={reviewing.selfie_url}
                      alt="Selfie"
                      className="w-full aspect-[3/4] object-cover rounded-lg bg-gray-100"
                    />
                  ) : (
                    <div className="w-full aspect-[3/4] rounded-lg bg-[#F7D7EA] flex items-center justify-center text-xs text-[#8A3976] text-center px-2">
                      No selfie available
                    </div>
                  )}
                </div>
              </div>

              <p className="text-xs text-gray-400">Verify the selfie matches the profile photo before approving.</p>

              <DialogFooter className="gap-2 flex-wrap">
                <Button variant="outline" onClick={closeReview} className="rounded-lg">
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setConfirmDecline(true)}
                  className="rounded-lg"
                  data-testid={`decline-selfie-${reviewing?.id}`}
                >
                  <X className="h-3.5 w-3.5 mr-1" />
                  Reject
                </Button>
                <Button
                  onClick={approveSelfie}
                  disabled={!reviewing?.selfie_url}
                  className="rounded-lg bg-[#C857A8] hover:bg-[#B54E9A] text-white disabled:opacity-50"
                  data-testid={`approve-selfie-${reviewing?.id}`}
                >
                  <Check className="h-3.5 w-3.5 mr-1" />
                  Approve
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Referral Code Confirmation */}
      <Dialog open={!!confirmDeleteCode} onOpenChange={() => setConfirmDeleteCode(null)}>
        <DialogContent className="max-w-sm rounded-xl">
          <DialogHeader>
            <DialogTitle>Delete code?</DialogTitle>
            <DialogDescription>
              Delete <code className="font-mono font-semibold">{confirmDeleteCode?.code}</code>? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDeleteCode(null)} className="rounded-lg">Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteCode} className="rounded-lg" data-testid="confirm-delete-code-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

function StatCard({ icon, label, value, testId }) {
  return (
    <div className="p-4 border border-gray-200 rounded-lg">
      <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
        {icon}
        {label}
      </div>
      <p className="text-2xl font-bold" data-testid={testId}>{value}</p>
    </div>
  );
}

function SelfieCard({ profile, onReview }) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`selfie-card-${profile.id}`}>
      <div className="grid grid-cols-2">
        <div className="relative">
          {profile.cover_url ? (
            <img
              src={profile.cover_url}
              alt="Profile"
              className="w-full aspect-[3/4] object-cover bg-gray-100"
            />
          ) : (
            <div className="w-full aspect-[3/4] bg-gray-100 flex items-center justify-center text-xs text-gray-400">
              No photo
            </div>
          )}
          <span className="absolute bottom-1 left-1 bg-black/50 text-white text-[9px] px-1.5 py-0.5 rounded">
            Profile
          </span>
        </div>
        <div className="relative">
          {profile.selfie_url ? (
            <img
              src={profile.selfie_url}
              alt="Selfie"
              className="w-full aspect-[3/4] object-cover bg-gray-100"
            />
          ) : (
            <div className="w-full aspect-[3/4] bg-[#F7D7EA] flex items-center justify-center text-[10px] text-[#8A3976] text-center px-2">
              No selfie
            </div>
          )}
          <span className="absolute bottom-1 left-1 bg-black/50 text-white text-[9px] px-1.5 py-0.5 rounded">
            Selfie
          </span>
        </div>
      </div>
      <div className="p-3">
        <p className="font-medium text-sm">{profile.display_name} {profile.last_initial}</p>
        <p className="text-[10px] text-gray-400 font-mono mt-0.5">{profile.id?.slice(0, 8)}…</p>
        <p className="text-xs text-gray-500 mt-0.5">{profile.city}{profile.sect ? ` · ${profile.sect}` : ''}</p>
        {profile.selfie_submitted_at && (
          <p className="text-[10px] text-gray-400 mt-0.5">
            Submitted {new Date(profile.selfie_submitted_at).toLocaleDateString()}
          </p>
        )}
        <Button
          onClick={onReview}
          size="sm"
          className="w-full mt-3 bg-[#C857A8] hover:bg-[#B54E9A] text-white rounded-lg text-xs h-8"
          data-testid={`review-${profile.id}`}
        >
          Review
        </Button>
      </div>
    </div>
  );
}

function ReferralCodeRow({ rc, onDelete }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(rc.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <tr className="border-b border-gray-50 last:border-0" data-testid={`referral-code-row-${rc.id}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <code className="font-mono text-sm text-gray-900">{rc.code}</code>
          <button onClick={copy} className="text-gray-400 hover:text-gray-600 transition-colors" title="Copy">
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        </div>
      </td>
      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
        {new Date(rc.created_at).toLocaleDateString()} {new Date(rc.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </td>
      <td className="px-4 py-3">
        {rc.used_by
          ? <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[11px] font-medium">Used</span>
          : <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[11px] font-medium">Available</span>
        }
      </td>
      <td className="px-4 py-3">
        {!rc.used_by && (
          <button onClick={onDelete} className="text-gray-400 hover:text-red-500 transition-colors" data-testid={`delete-code-${rc.id}`} title="Delete">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </td>
    </tr>
  );
}

const REPORT_REASON_COLORS = {
  'Not Jain': 'bg-amber-100 text-amber-700',
  'Fake profile': 'bg-red-100 text-red-700',
  'Harassment': 'bg-red-100 text-red-700',
  'Inappropriate content': 'bg-orange-100 text-orange-700',
};

function ReportedCard({ profile, onRefresh }) {
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [working, setWorking] = useState(false);
  const [viewProfile, setViewProfile] = useState(false);

  const handleSuspend = async () => {
    console.log('suspend clicked for:', profile.id);
    setWorking(true);
    const result = await db.suspendProfile(profile.id);
    setWorking(false);
    if (result.success) { toast('Profile suspended'); onRefresh(); }
    else toast(`Error suspending: ${result.error}`);
    setConfirmSuspend(false);
  };

  const handleUnsuspend = async () => {
    console.log('suspend clicked for:', profile.id);
    setWorking(true);
    const result = await db.unsuspendProfile(profile.id);
    setWorking(false);
    if (result.success) { toast('Profile restored'); onRefresh(); }
    else toast(`Error restoring: ${result.error}`);
  };

  const displayReports = profile.reports?.slice(0, 5) || [];
  const extraCount = (profile.reports?.length || 0) - displayReports.length;

  return (
    <>
      <div className="border border-orange-100 rounded-xl p-4" data-testid={`reported-card-${profile.id}`}>
        {/* Profile info row */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 shrink-0">
            {profile.cover_url
              ? <img src={profile.cover_url} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-sm font-bold text-gray-400">{profile.display_name?.[0]}</div>
            }
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {profile.display_name}{profile.last_initial ? ` ${profile.last_initial}.` : ''}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {profile.city}{profile.sect ? ` · ${profile.sect}` : ''}
            </p>
          </div>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {(profile.report_count || 0) > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[11px] font-semibold">
              {profile.report_count} report{profile.report_count !== 1 ? 's' : ''}
            </span>
          )}
          {profile.vouch_status && profile.vouch_status !== 'none' && (
            <span className="px-2 py-0.5 rounded-full bg-[#FBE7F3] text-[#C857A8] text-[11px] font-medium capitalize">
              {profile.vouch_status}
            </span>
          )}
          {profile.admin_blocked && (
            <span className="px-2 py-0.5 rounded-full bg-red-600 text-white text-[11px] font-semibold">
              Suspended
            </span>
          )}
        </div>

        {/* Reports list */}
        {displayReports.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {displayReports.map(r => (
              <div key={r.id} className="flex items-center gap-2 text-xs">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${REPORT_REASON_COLORS[r.reason] || 'bg-gray-100 text-gray-600'}`}>
                  {r.reason}
                </span>
                <span className="text-gray-500 truncate">{r.reporter_name}</span>
                <span className="text-gray-400 shrink-0 ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
            ))}
            {extraCount > 0 && (
              <p className="text-xs text-gray-400">+{extraCount} more report{extraCount !== 1 ? 's' : ''}</p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => setViewProfile(true)}
            className="flex-1 h-8 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            View Full Profile
          </button>
          {!profile.admin_blocked ? (
            confirmSuspend ? (
              <div className="flex gap-1 flex-1">
                <button
                  onClick={() => setConfirmSuspend(false)}
                  className="flex-1 h-8 rounded-lg border border-gray-200 text-xs font-medium text-gray-500 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSuspend}
                  disabled={working}
                  className="flex-1 h-8 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-60 transition-colors"
                  data-testid={`confirm-suspend-${profile.id}`}
                >
                  {working ? <Loader2 className="h-3 w-3 animate-spin mx-auto" /> : 'Yes'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmSuspend(true)}
                className="flex-1 h-8 rounded-lg bg-red-50 text-red-700 border border-red-200 text-xs font-semibold hover:bg-red-100 transition-colors"
                data-testid={`suspend-${profile.id}`}
              >
                Suspend Profile
              </button>
            )
          ) : (
            <button
              onClick={handleUnsuspend}
              disabled={working}
              className="flex-1 h-8 rounded-lg border border-gray-200 text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-60 transition-colors"
              data-testid={`unsuspend-${profile.id}`}
            >
              {working ? <Loader2 className="h-3 w-3 animate-spin mx-auto" /> : 'Unsuspend'}
            </button>
          )}
        </div>
      </div>

      {/* View Full Profile Modal */}
      <Dialog open={viewProfile} onOpenChange={setViewProfile}>
        <DialogContent className="max-w-sm rounded-xl max-h-[88vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-2 shrink-0">
            <DialogTitle>{profile.display_name}{profile.last_initial ? ` ${profile.last_initial}.` : ''}</DialogTitle>
            <DialogDescription>{profile.city}{profile.sect ? ` · ${profile.sect}` : ''}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-4 pt-0 space-y-3">
            {profile.cover_url && (
              <img src={profile.cover_url} alt="" className="w-full aspect-[3/4] object-cover rounded-lg" />
            )}
            <div className="space-y-1.5 text-sm">
              {profile.gender && <p><span className="font-medium text-gray-700">Gender:</span> <span className="text-gray-600">{profile.gender}</span></p>}
              {profile.sect && <p><span className="font-medium text-gray-700">Sect:</span> <span className="text-gray-600">{profile.sect}</span></p>}
              {profile.city && <p><span className="font-medium text-gray-700">City:</span> <span className="text-gray-600">{profile.city}</span></p>}
              {profile.vouch_status && <p><span className="font-medium text-gray-700">Status:</span> <span className="text-gray-600 capitalize">{profile.vouch_status}</span></p>}
              {(profile.report_count || 0) > 0 && <p><span className="font-medium text-gray-700">Reports:</span> <span className="text-red-600 font-semibold">{profile.report_count}</span></p>}
              {profile.admin_blocked && <p className="text-red-600 font-medium">This profile is suspended</p>}
            </div>
            {profile.reports?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">All Reports</p>
                <div className="space-y-1.5">
                  {profile.reports.map(r => (
                    <div key={r.id} className="flex items-center gap-2 text-xs">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${REPORT_REASON_COLORS[r.reason] || 'bg-gray-100 text-gray-600'}`}>
                        {r.reason}
                      </span>
                      <span className="text-gray-500 truncate">{r.reporter_name}</span>
                      <span className="text-gray-400 shrink-0 ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
